//====== WR data 03/09/2026
// Motivo da alteração: porte do SmartFlow Importer do Portal LiveModa (portal-b1-moda-front) para
// a plataforma SmartFlow. No portal, este arquivo (services/smartflow/smartflowApi.js +
// services/smartflow/smartflowSequenciaisApi.js) falava direto com o Supabase (supabase-js) e com
// Cloud Functions Firebase pra ler o Drive. Aqui tudo passa pelo backend próprio
// (smartflow-backend, rotas /api/smartflow-importer) via services/api.js.
//
// A CONFIG do Importer (2 pastas do Drive, Grupo do PN, sequência do CardCode) fica nos dados do
// SmartFlowProject — lida via /smartflowproject/read/:id e salva via /smartflowproject/update/:id
// (modal "Alterando Projeto"). Por isso NÃO há funções de config aqui; só o avanço do contador de
// sequência (atômico, no backend) e o helper puro lerSequenciaDoProjeto().
//
// A validação de CNPJ na Receita (BrasilAPI) continua sendo chamada direto do navegador — API
// pública, sem chave, CORS aberto, nada a proteger (idêntico ao portal).
//======
import api from "services/api"

const BASE = "/smartflow-importer"
const BRASILAPI_CNPJ_URL = "https://brasilapi.com.br/api/cnpj/v1"

//====== WR data 09/09/2026
// Motivo da alteração: o backend DEV roda no plano free do Render, que dorme após ~15 min ocioso
// e a 1ª requisição depois disso pode dar "Network Error" (connection drop durante o cold start).
// Isso deixava operações pela metade (ex.: excluir some do SAP mas não do espelho). Retry curto
// só pra erro de REDE (sem `response`) — nunca repete um 4xx/5xx de verdade, que é resposta do
// servidor. Espera crescente (2s, 5s, 9s) cobrindo o tempo típico de acordar o Render.
//======
async function comRetry(fn, tentativas = 4) {
  const esperas = [0, 2000, 5000, 9000]
  let ultimoErro
  for (let i = 0; i < tentativas; i++) {
    if (esperas[i]) await new Promise((r) => setTimeout(r, esperas[i]))
    try {
      return await fn()
    } catch (error) {
      ultimoErro = error
      const semResposta = !error?.response // erro de rede / timeout — vale repetir
      if (!semResposta) throw error // resposta do servidor (4xx/5xx) — não repete
    }
  }
  throw ultimoErro
}

//====== Config do Importer (dados do projeto) ======

/**
 * Lê a config de sequência do CardCode de um papel, direto do objeto do projeto já carregado
 * (project.smartflowImporter.<papel>.pn*), aplicando os defaults (F- / C-). Sem chamada de rede.
 * @param {Object} project doc do SmartFlowProject
 * @param {"fornecedor"|"cliente"} [tipo]
 */
export function lerSequenciaDoProjeto(project, tipo = "fornecedor") {
  const papel = tipo === "cliente" ? "cliente" : "fornecedor"
  const p = project?.smartflowImporter?.[papel] || {}
  return {
    usaPrefixo: p.pnUsaPrefixo ?? true,
    prefixo: p.pnPrefixo || (papel === "cliente" ? "C-" : "F-"),
    // pn* podem vir como string (campo de texto na config) ou number (persistido) — coage.
    qtdCaracteresSequencia: Number(p.pnQtdCaracteresSequencia) || 5,
    proximoNumero: Number(p.pnProximoNumero) || 1,
  }
}

/**
 * Avança o contador de sequência do CardCode em `quantidade` (atômico no backend) — chamado 1x
 * por LOTE, depois da gravação real no SAP. Nunca lança erro pra cima (falha silenciosa, só loga).
 */
export async function incrementarProximoNumeroParceiro(projectId, quantidade, tipo = "fornecedor") {
  if (!quantidade || quantidade <= 0) return
  try {
    await comRetry(() => api.post(`${BASE}/sequencia/${projectId}/incrementar`, { tipo, quantidade }))
  } catch (error) {
    console.error("[SmartFlow] Falha ao avançar o próximo número do Parceiro de Negócio:", error)
  }
}

//====== Google Drive (via backend) ======

/**
 * @param {string} folderInput link ou ID da pasta do Google Drive
 * @return {Promise<{ connected: boolean, folderId: string, folderName: string }>}
 */
export async function validateDriveFolder(folderInput) {
  try {
    const { data } = await comRetry(() => api.post(`${BASE}/drive/validate-folder`, { folderInput }))
    return data
  } catch (error) {
    throw new Error(error?.response?.data?.error || error?.message || "Falha ao validar a pasta do Drive.")
  }
}

/**
 * @param {string} folderInput ID (ou link) da pasta já validada
 * @param {"fornecedor"|"cliente"} [tipo]
 * @return {Promise<{ notas: Array, naoIdentificados: Array, totalArquivos: number, totalXml: number, totalIdentificados: number }>}
 */
export async function listDriveFiles(folderInput, tipo = "fornecedor") {
  try {
    const { data } = await comRetry(() => api.post(`${BASE}/drive/list-files`, { folderInput, tipo }))
    return data
  } catch (error) {
    throw new Error(error?.response?.data?.error || error?.message || "Falha ao listar os arquivos do Drive.")
  }
}

//====== WR data 16/09/2026
// Motivo da alteração: pedido do usuário — pasta com centenas de notas ficava sem NENHUM feedback
// até tudo terminar. Versão em JOB (polling) de listDriveFiles: inicia o job no backend, consulta
// o progresso a cada 1,5s via onProgress(concluidos, total), e resolve com o MESMO formato de
// retorno de listDriveFiles quando terminar — quem chama não precisa saber que por baixo é polling.
// @param {string} folderInput
// @param {"fornecedor"|"cliente"} [tipo]
// @param {(concluidos:number, total:number) => void} [onProgress]
// @return {Promise<{ notas: Array, naoIdentificados: Array, totalArquivos: number, totalXml: number, totalIdentificados: number }>}
export async function listDriveFilesComProgresso(folderInput, tipo = "fornecedor", onProgress) {
  const INTERVALO_POLL_MS = 1500
  let jobId
  try {
    const { data } = await comRetry(() =>
      api.post(`${BASE}/drive/list-files-job`, { folderInput, tipo })
    )
    jobId = data.jobId
  } catch (error) {
    throw new Error(error?.response?.data?.error || error?.message || "Falha ao iniciar a leitura do Drive.")
  }

  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, INTERVALO_POLL_MS))
    let status
    try {
      const { data } = await comRetry(() => api.get(`${BASE}/drive/list-files-job/${jobId}`))
      status = data
    } catch (error) {
      throw new Error(error?.response?.data?.error || error?.message || "Falha ao consultar o progresso da leitura.")
    }

    if (onProgress) onProgress(status.progresso?.concluidos || 0, status.progresso?.total || 0)

    if (status.status === "concluido") return status.resultado
    if (status.status === "erro") throw new Error(status.erro || "Falha ao listar os arquivos do Drive.")
    // "processando" -> continua o loop
  }
}

//====== Receita Federal (BrasilAPI, direto do navegador) ======

/**
 * @param {string} cnpj só dígitos ou já formatado
 * @return {Promise<Object>} corpo cru da resposta da BrasilAPI
 */
export async function validarReceitaCnpj(cnpj) {
  const digits = String(cnpj || "").replace(/\D/g, "")
  const response = await fetch(`${BRASILAPI_CNPJ_URL}/${digits}`)

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`CNPJ ${cnpj} não encontrado na Receita Federal.`)
    }
    throw new Error(`Falha ao consultar o CNPJ ${cnpj} na BrasilAPI (HTTP ${response.status}).`)
  }
  return response.json()
}

//====== Parceiros — staging ======

/**
 * Upsert em lote por {project, cardCode}. `rows` são as linhas no formato SAP (saída de
 * montarParceiroSap) — o backend mapeia pros campos camelCase do model.
 * @return {Promise<Array<Object>>} docs gravados (camelCase, incluindo _id)
 */
export async function salvarParceirosStaging(projectId, rows, preparadoPor) {
  if (!rows?.length) return []
  const { data } = await comRetry(() => api.post(`${BASE}/parceiros/${projectId}/staging`, { rows, preparadoPor }))
  return data
}

export const PARCEIROS_STAGING_PAGE_SIZE = 20

/**
 * Lista/busca/pagina a staging de um projeto (tela "Parceiros de Negócio Importados").
 * @param {{ projectId: string, termo?: string, page?: number }} params
 * @return {Promise<{ parceiros: Array<Object>, total: number, pageSize: number }>}
 */
export async function listarParceirosStaging({ projectId, termo, page = 1 } = {}) {
  const { data } = await api.get(`${BASE}/parceiros/${projectId}/staging`, {
    params: { termo: termo || undefined, page },
  })
  return data
}

/**
 * Exclusão FÍSICA de um parceiro da staging — quem/quando fica no log. NÃO exclui do SAP (isso é
 * excluirParceiroDoSap em smartflowSapWrite.js, chamado à parte pela tela).
 * @param {Object} parceiro doc completo (precisa do _id)
 */
export async function excluirParceiroStaging(parceiro, excluidoPor) {
  await comRetry(() => api.delete(`${BASE}/parceiros/staging/${parceiro._id}`, { data: { excluidoPor } }))
}

/**
 * Mapa { [cnpjDigits]: cardCode } de QUALQUER staging do projeto — aviso na aba "Notas identificadas".
 */
export async function buscarParceirosJaImportadosPorCnpj(projectId, cnpjs) {
  const { data } = await api.post(`${BASE}/parceiros/${projectId}/ja-importados`, { cnpjs: cnpjs || [] })
  return data || {}
}

/**
 * Mapa { [cnpjDigits]: cardCode } só dos já gravados no SAP (status gravado_sap).
 */
export async function buscarParceirosGravadosNoSapPorCnpj(projectId, cnpjs) {
  const { data } = await api.post(`${BASE}/parceiros/${projectId}/gravados-sap`, { cnpjs: cnpjs || [] })
  return data || {}
}

/**
 * Registra no backend que um parceiro foi gravado de verdade no SAP (status gravado_sap +
 * gravado_em/gravado_por + log). Chamado por gravarParceiroNoSap após o POST/PATCH bem-sucedido.
 * @param {string} stagingId  _id do doc de staging
 * @param {{ gravadoPor?: string|null, cardCode?: string }} params
 */
export async function marcarParceiroGravadoNoSap(stagingId, { gravadoPor, cardCode } = {}) {
  const { data } = await comRetry(() => api.patch(`${BASE}/parceiros/staging/${stagingId}/gravado`, { gravadoPor, cardCode }))
  return data
}

//====== Municípios (OCNT) ======

/**
 * Resolve códigos IBGE pros AbsId internos da OCNT que o campo BPAddresses.County do SAP espera.
 * @param {Array<number|string>} ibgeCodes
 * @return {Promise<Object>} { [ibgeCode]: { absId:number, uf:string, nome:string } }
 */
export async function resolverMunicipiosSap(ibgeCodes) {
  if (!ibgeCodes?.length) return {}
  const { data } = await api.post(`${BASE}/municipios/resolve`, { ibgeCodes })
  return data || {}
}
